import { isRecord } from '@shared/utils/isRecord';

import type { VoiceFormDraft, VoiceSettingsObject } from './types';
import { resolveVoiceFormFields, VOICE_FORM_FIELDS } from './voiceSettings.utils';

const FORMAT = 'call-agent.voice-form/v1';
/** `form` replaces the whole form; `settings` merges only the values it carries. */
type VoiceFormTransferResult =
  | { kind: 'form' | 'settings'; value: VoiceFormDraft; error?: never }
  | { kind?: never; value?: never; error: 'invalidJson' | 'objectRequired' | 'invalidEnvelope' };

/**
 * Only editable fields cross the clipboard boundary; server identity is never transferred.
 * A version marker, not a voiceSettings-shaped heuristic, distinguishes exports from flat JSON.
 */
export const exportVoiceForm = (form: VoiceFormDraft): string => {
  const values: VoiceSettingsObject = { voiceSettings: form.voiceSettings };
  for (const key of VOICE_FORM_FIELDS) {
    if (Object.hasOwn(form, key)) values[key] = form[key];
  }
  return JSON.stringify({ $voiceForm: FORMAT, values }, null, 2);
};

/** Representable JSON copies win duplicates, as in Worker; other settings stay raw JSON. */
export const importVoiceForm = (text: string): VoiceFormTransferResult => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { error: 'invalidJson' };
  }
  if (!isRecord(parsed)) return { error: 'objectRequired' };
  if (parsed.$voiceForm !== FORMAT) {
    // Flat settings keep current metadata and credentials unless they carry them.
    const { fields, settings } = resolveVoiceFormFields({}, parsed);
    return { kind: 'settings', value: { ...fields, voiceSettings: settings } };
  }
  if (!isRecord(parsed.values) || !isRecord(parsed.values.voiceSettings)) {
    return { error: 'invalidEnvelope' };
  }
  const { fields, settings } = resolveVoiceFormFields(parsed.values, {
    ...parsed.values.voiceSettings,
  });
  // A full export replaces the entire form. Explicit nulls also clear saved API columns on edit.
  const next: VoiceFormDraft = {
    name: '',
    previewUrl: '',
    provider: '',
    providerDisplayName: '',
    voiceId: '',
    model: null,
    language: null,
    gender: '',
    accent: '',
    apiKey: null,
    baseUrl: null,
    ...fields,
    voiceSettings: settings,
  };
  return { kind: 'form', value: next };
};
